<script lang="ts">
  import ModalShell from './ModalShell.svelte';
  import { resolvePlaceUrl, newSessionToken } from './api';

  let {
    tripToken,
    onResolved,
    onClose,
  }: {
    tripToken: string;
    onResolved: (args: {
      placeId: string;
      displayName: string;
      sessionToken: string;
    }) => void;
    onClose: () => void;
  } = $props();

  let url: string = $state('');
  let resolving: boolean = $state(false);
  let error: string | null = $state(null);
  let helpOpen: boolean = $state(false);
  let inputEl: HTMLInputElement | null = $state(null);

  $effect(() => {
    inputEl?.focus();
  });

  async function submit() {
    const trimmed = url.trim();
    if (!trimmed || resolving) return;
    resolving = true;
    error = null;
    const sessionToken = newSessionToken();
    try {
      const { placeId, displayName } = await resolvePlaceUrl(tripToken, trimmed);
      onResolved({ placeId, displayName, sessionToken });
    } catch (e) {
      error = e instanceof Error ? e.message : 'Could not resolve link';
      resolving = false;
    }
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void submit();
    }
  }
</script>

<ModalShell title="Paste a Maps link" {onClose} maxWidth={460}>
  <p class="lede">
    Drop a Google or Apple Maps link and we'll find the place.
  </p>

  <input
    bind:this={inputEl}
    bind:value={url}
    type="url"
    placeholder="https://maps.app.goo.gl/..."
    autocomplete="off"
    spellcheck="false"
    onkeydown={onKeyDown}
    disabled={resolving}
    class="url-input"
  />

  {#if error}
    <p class="error" role="alert">{error}</p>
  {/if}

  <button
    type="button"
    class="help-toggle"
    aria-expanded={helpOpen}
    onclick={() => (helpOpen = !helpOpen)}
  >
    How do I get a link?
  </button>
  {#if helpOpen}
    <ul class="help">
      <li><strong>On your phone:</strong> open the place in Google or Apple Maps → tap <em>Share</em> → <em>Copy link</em>.</li>
      <li><strong>On desktop:</strong> open the place in Google Maps → click the <em>Share</em> icon → <em>Copy link</em>.</li>
      <li>You can also paste a plain place name — we'll search for it.</li>
    </ul>
  {/if}

  <div class="actions">
    <button type="button" class="btn-ghost" onclick={onClose} disabled={resolving}>
      Cancel
    </button>
    <button
      type="button"
      class="btn-primary"
      onclick={submit}
      disabled={resolving || url.trim().length === 0}
    >
      {resolving ? 'Finding…' : 'Find place'}
    </button>
  </div>
</ModalShell>

<style>
  .lede {
    margin: 0;
    color: var(--color-muted);
    font-size: 14px;
  }
  .url-input {
    width: 100%;
    padding: 0.625rem 0.75rem;
    font: inherit;
    /* 16px to avoid iOS Safari auto-zoom-on-focus. */
    font-size: 16px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-surface-soft);
    color: var(--color-ink);
  }
  .url-input:focus {
    outline: none;
    border-color: var(--color-primary);
    background: var(--color-canvas);
  }
  .error {
    margin: 0;
    color: var(--color-primary);
    font-size: 13px;
  }
  .help-toggle {
    align-self: flex-start;
    background: transparent;
    border: 0;
    padding: 0;
    color: var(--color-muted);
    font-size: 13px;
    text-decoration: underline;
    cursor: pointer;
  }
  .help-toggle:hover {
    color: var(--color-ink);
  }
  .help {
    margin: 0;
    padding-left: 1.1rem;
    color: var(--color-muted);
    font-size: 13px;
    line-height: 1.5;
  }
  .help li {
    margin: 0.25rem 0;
  }
  .help strong {
    color: var(--color-ink);
    font-weight: 600;
  }
  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.25rem;
  }
  .btn-ghost,
  .btn-primary {
    padding: 0.5rem 0.875rem;
    border-radius: var(--radius-sm);
    font: inherit;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
    border: 1px solid transparent;
  }
  .btn-ghost {
    background: transparent;
    border-color: var(--color-border);
    color: var(--color-ink);
  }
  .btn-ghost:hover {
    background: var(--color-surface-soft);
  }
  .btn-primary {
    background: var(--color-primary);
    color: white;
  }
  .btn-primary:hover {
    filter: brightness(0.95);
  }
  .btn-primary:disabled,
  .btn-ghost:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
</style>
